import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Phone, PhoneOff, PhoneCall, Mic, MicOff, Pause, Play, Delete, X, ChevronUp, ChevronDown, Power,
  Wifi, WifiOff, Loader2, Building2, History, PhoneIncoming, PhoneOutgoing, RefreshCw, UserPlus, UserRound,
  Sparkles,
} from "lucide-react";
import { useTwilio } from "@/context/TwilioVoice";
import { useAuth } from "@/context/CrmAuth";
import UnknownCallerModal from "@/crm/components/telephony/UnknownCallerModal";
import { showCopilot } from "@/crm/components/copilot/CopilotHost";
import { barLayout } from "@/crm/components/telephony/barLayout";
import api from "@/lib/crmApi";
import { cn } from "@/lib/utils";
import { FloatingWindowDock, useFloatingWindows } from "@/crm/components/FloatingWindow";
import Modal from "@/crm/components/Modal";

// Lettres sous les chiffres : repère visuel d'un vrai clavier téléphonique.
const KEYPAD = [
  ["1", ""], ["2", "ABC"], ["3", "DEF"],
  ["4", "GHI"], ["5", "JKL"], ["6", "MNO"],
  ["7", "PQRS"], ["8", "TUV"], ["9", "WXYZ"],
  ["*", ""], ["0", "+"], ["#", ""],
];

// Couleur du résultat d'un appel dans l'historique du composeur.
const RESULT_CLS = {
  REPONDU: "text-[#008060]", TERMINE: "text-[#008060]", MANQUE: "text-[#d72c0d]",
  REJETE: "text-[#d72c0d]", RENVOI_SPECIAL: "text-[#d72c0d]", MESSAGERIE: "text-[#8a6116]",
  ABANDONNE: "text-[#8a6116]", EN_COURS: "text-[#005bd3]",
};

// Libellés d'état. Le nom du fournisseur vient du backend : la barre affichait
// « Signal Twilio perdu » et « Twilio non configuré » sous Telnyx.
const statusMeta = (fournisseur) => ({
  ready: { label: "Téléphone connecté", dot: "bg-[#008060]", icon: Wifi },
  connecting: { label: "Connexion…", dot: "bg-amber-400", icon: Loader2 },
  reconnecting: { label: `⚠ Signal ${fournisseur ? `${fournisseur} ` : "téléphonique "}perdu — reconnexion…`,
    dot: "bg-orange-500", icon: Loader2 },
  offline: { label: "Téléphone déconnecté", dot: "bg-slate-400", icon: WifiOff },
  unconfigured: { label: fournisseur ? `${fournisseur} non configuré` : "Téléphonie non configurée",
    dot: "bg-slate-500", icon: WifiOff },
  error: { label: "Erreur téléphonie", dot: "bg-rose-500", icon: WifiOff },
});

// « Client injoignable » en tête : c'est l'issue la plus fréquente d'un appel
// sortant (messagerie, sonnerie dans le vide), il manquait au compte-rendu.
const OUTCOMES = [
  "Client injoignable", "Intéressé", "Pas intéressé", "À rappeler",
  "Rendez-vous", "Mauvais numéro", "Demande d'information", "Autre",
];

const fmtDur = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

// Gabarits de la barre sombre et du composeur (palette du handoff).
const BAR_GHOST = "inline-flex h-10 items-center justify-center whitespace-nowrap rounded-lg border border-white/[.14] bg-white/[.06] text-[13px] font-medium text-[#e6e8ec] transition-colors hover:bg-white/[.12]";
const BAR_PRIMARY = "inline-flex h-10 items-center justify-center whitespace-nowrap rounded-lg bg-[#005bd3] text-[13px] font-semibold text-white transition-all hover:bg-[#00479f] active:scale-[.98]";
const CALL_ICON_BTN = "inline-flex h-[30px] w-[30px] items-center justify-center rounded-[7px] text-[#eceef2] transition-colors";
const DIALER_ICON_BTN = "inline-flex h-7 w-[34px] items-center justify-center rounded-md text-[#5b6270] transition-colors hover:bg-[rgba(15,23,42,.06)]";
const FIELD = "w-full rounded-lg border border-[#d0d0d0] bg-white px-3 py-2 text-sm text-[#0b1524] outline-none transition-all focus:border-[#005bd3] focus:ring-[3px] focus:ring-[rgba(0,91,211,.14)]";

function DispositionModal({ ended, onClose }) {
  const [outcome, setOutcome] = useState("");
  const [note, setNote] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/twilio/calls/disposition", {
        ref: ended.ref, outcome, note, next_action: nextAction,
        next_action_date: nextDate ? new Date(nextDate).toISOString() : null,
        call_duration: ended.seconds, result: "TERMINE",
      });
      toast.success("Compte-rendu d'appel enregistré");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible");
    } finally { setSaving(false); }
  };

  // Ni clic sur le fond ni Échap : le compte-rendu se ferme par sa croix, pour
  // ne pas perdre une saisie d'un geste involontaire.
  return (
    <Modal onClose={onClose} zClassName="z-[80]" padded={false} label="Compte-rendu d'appel"
      closeOnOverlayClick={false} closeOnEscape={false} testid="call-disposition-modal">
      <div className="flex items-center justify-between bg-[#16171a] px-5 py-4 text-white">
        <div>
          <div className="text-xs uppercase tracking-wide text-[#9aa0ab]">Appel terminé · {fmtDur(ended.seconds || 0)}</div>
          <div className="font-semibold">{ended.company_name || ended.number}</div>
        </div>
        <button onClick={onClose} data-testid="disposition-close" aria-label="Fermer"
          className="rounded-md p-1 text-[#9aa0ab] transition-colors hover:bg-white/10 hover:text-white"><X size={18} /></button>
      </div>
      <div className="space-y-4 p-5">
        <div>
          <label className="text-xs font-semibold uppercase text-[#5b6270]">Résultat</label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {OUTCOMES.map((o) => (
              <button key={o} data-testid={`disposition-outcome-${o}`} onClick={() => setOutcome(o)}
                className={cn("rounded-lg border px-3 py-2 text-left text-sm transition-colors",
                  outcome === o ? "border-[#005bd3] bg-[#e8f1fd] font-medium text-[#00479f]" : "border-[#e3e3e3] hover:bg-[#fafafa]")}>
                {o}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase text-[#5b6270]">Note</label>
          <textarea data-testid="disposition-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
            className={cn(FIELD, "mt-1")} placeholder="Compte-rendu de l'appel…" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs font-semibold uppercase text-[#5b6270]">Prochaine action</label>
            <input data-testid="disposition-next-action" value={nextAction} onChange={(e) => setNextAction(e.target.value)}
              className={cn(FIELD, "mt-1")} placeholder="Ex. Envoyer devis" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-[#5b6270]">Échéance</label>
            <input type="date" data-testid="disposition-next-date" value={nextDate} onChange={(e) => setNextDate(e.target.value)}
              className={cn(FIELD, "mt-1")} />
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 border-t border-[#e3e3e3] px-5 py-3">
        <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-[#4a5261] transition-colors hover:bg-[#f2f4f7]">Ignorer</button>
        <button onClick={save} disabled={saving} data-testid="disposition-save"
          className="rounded-lg bg-[#005bd3] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#00479f] disabled:opacity-50">
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>
    </Modal>
  );
}

const SENS_FILTRES = [["tous", "Tous"], ["ENTRANT", "Entrants"], ["SORTANT", "Sortants"]];

const fmtWhen = (iso) => (iso
  ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })
    + " " + new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
  : "");

function CallHistory({ items, loading, onReload, onPick, onCall, onCreate, canCall, isAdmin, sens, onSens }) {
  if (loading && !items) {
    return (
      <div className="flex items-center justify-center gap-2 py-8 text-sm text-[#8a8f98]">
        <Loader2 size={15} className="animate-spin" /> Chargement…
      </div>
    );
  }
  return (
    <div data-testid="dialer-history">
      <div className="mb-2 flex items-center gap-2">
        <div className="flex gap-1 rounded-lg border border-[rgba(15,23,42,.08)] bg-white p-0.5" data-testid="dialer-history-filter">
          {SENS_FILTRES.map(([k, label]) => (
            <button key={k} data-testid={`dialer-history-filter-${k}`} onClick={() => onSens(k)}
              className={cn("rounded-md px-2.5 py-1 text-[11.5px] font-medium transition-colors",
                sens === k ? "bg-[#16171a] text-white" : "text-[#5b6270] hover:bg-[#f2f4f7]")}>
              {label}
            </button>
          ))}
        </div>
        <span className="ml-auto truncate text-[11.5px] text-[#8a8f98]">
          {isAdmin ? "Tous les appels" : "Vos appels"} · {items?.length || 0}
        </span>
        <button onClick={onReload} disabled={loading} data-testid="dialer-history-reload" aria-label="Rafraîchir l'historique"
          className="shrink-0 rounded-md p-1 text-[#8a8f98] transition-colors hover:bg-[#f2f4f7] hover:text-[#0b1524]">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
        </button>
      </div>
      {!items?.length ? (
        <p className="py-8 text-center text-sm text-[#8a8f98]">
          {sens === "ENTRANT" ? "Aucun appel entrant." : sens === "SORTANT" ? "Aucun appel sortant."
            : "Aucun appel enregistré."}
        </p>
      ) : (
        <ul className="flex flex-col">
          {items.map((c) => (
            <li key={c.id} data-testid="dialer-history-row"
              className="flex items-center gap-2.5 rounded-lg border-b border-[rgba(15,23,42,.05)] px-1.5 py-2 transition-colors hover:bg-[#f2f4f7]/70">
              <span className="shrink-0 text-[#4a5261]">
                {c.direction === "SORTANT" ? <PhoneOutgoing size={15} /> : <PhoneIncoming size={15} />}
              </span>
              <button onClick={() => onPick(c.phone_number)} className="min-w-0 flex-1 text-left">
                {/* L'identité prime sur le numéro : une fois la fiche créée, on
                    reconnaît l'appelant d'un coup d'œil. */}
                <span className="block truncate text-[13px] font-semibold text-[#1f2530]">
                  {c.contact_name || c.company_name || c.phone_number}
                </span>
                <span className="block truncate text-[11px] text-[#8a8f98]">
                  {(c.contact_name || c.company_name)
                    ? <span className="font-mono">{c.phone_number}</span>
                    : (c.answered_by_name && isAdmin ? "" : "Numéro inconnu")}
                  {c.contact_name && c.company_name ? ` · ${c.company_name}` : ""}
                  {c.answered_by_name && isAdmin ? `${(c.contact_name || c.company_name) ? " · " : ""}${c.answered_by_name}` : ""}
                </span>
              </button>
              <span className="shrink-0 text-right">
                <span className={cn("block text-[11px] font-semibold", RESULT_CLS[c.result] || "text-[#5b6270]")}>
                  {c.result}
                </span>
                <span className="block text-[11px] text-[#8a8f98]">{fmtWhen(c.created_at)}</span>
              </span>
              {/* Le critère est le NOM affiché, pas l'identifiant : un appel
                  pointant vers une fiche supprimée n'affiche aucun nom et doit
                  rester re-rattachable. */}
              {!c.contact_name && !c.company_name && (
                <button onClick={() => onCreate(c.phone_number)} data-testid="dialer-history-create"
                  title={`Créer une fiche contact pour ${c.phone_number}`}
                  className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[7px] text-[#005bd3] transition-colors hover:bg-[#e8f1fd]">
                  <UserPlus size={15} />
                </button>
              )}
              <button onClick={() => onCall(c)} disabled={!canCall} data-testid="dialer-history-call"
                title={canCall ? `Rappeler ${c.phone_number}` : "Téléphone non connecté"}
                className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[7px] text-[#008060] transition-colors hover:bg-[#e7f5f1] disabled:opacity-40 disabled:hover:bg-transparent">
                <Phone size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function SoftphoneBar() {
  const tw = useTwilio();
  const { user } = useAuth();
  const fw = useFloatingWindows();
  const [open, setOpen] = useState(false);
  const [onglet, setOnglet] = useState("clavier");
  const [dial, setDial] = useState("");
  const [lookupName, setLookupName] = useState(null);
  const [historique, setHistorique] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [sens, setSens] = useState("tous"); // tous | ENTRANT | SORTANT
  const [aCreer, setACreer] = useState(null); // numéro inconnu à transformer en fiche
  const [suggestions, setSuggestions] = useState([]);
  const [cible, setCible] = useState(null); // fiche choisie : l'appel sera rattaché
  const rechercheTimer = useRef(null);

  // Largeur réellement disponible pour la barre — pas celle de l'écran : la
  // barre latérale en prend une part sur desktop, et le mode rail la change.
  // C'est elle qui pilote les règles d'adaptation (barLayout).
  const [barEl, setBarEl] = useState(null);
  const [vw, setVw] = useState(() => (typeof window === "undefined" ? 1280 : window.innerWidth));
  useEffect(() => {
    if (!barEl) return undefined;
    const measure = () => setVw(barEl.offsetWidth || window.innerWidth);
    measure();
    window.addEventListener("resize", measure);
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(barEl);
    }
    return () => { window.removeEventListener("resize", measure); if (ro) ro.disconnect(); };
  }, [barEl]);

  // Le périmètre est décidé par le backend : un commercial ne reçoit que ses
  // appels, un manager ou un admin les reçoit tous. Rien à filtrer ici, et
  // aucun risque qu'un oubli côté écran expose plus que prévu.
  const chargerHistorique = useCallback(async () => {
    setChargement(true);
    try {
      // Le tri entrant/sortant est fait par la requête, pas après coup : sinon
      // « 40 derniers appels » filtrés côté écran pourrait n'en afficher que
      // trois, les autres étant simplement hors de la page ramenée.
      const params = new URLSearchParams({ limit: 40 });
      if (sens !== "tous") params.set("direction", sens);
      const { data } = await api.get(`/telephony/calls?${params}`);
      setHistorique(data || []);
    } catch { setHistorique([]); }
    finally { setChargement(false); }
  }, [sens]);

  useEffect(() => {
    if (open && onglet === "historique" && !chargement) chargerHistorique();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onglet, chargerHistorique]);

  // Rafraîchissement tant que l'historique est sous les yeux — et seulement là,
  // pour ne pas interroger le serveur en continu depuis chaque onglet ouvert.
  useEffect(() => {
    if (!(open && onglet === "historique")) return undefined;
    const t = setInterval(chargerHistorique, 15000);
    return () => clearInterval(t);
  }, [open, onglet, chargerHistorique]);

  // Un appel qui vient de se terminer doit apparaître tout de suite.
  const dernierAppel = tw?.endedCall;
  useEffect(() => {
    if (dernierAppel && open && onglet === "historique") chargerHistorique();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dernierAppel]);

  if (!tw) return null;
  // Une seule source pour les capacités : celles que le contexte tient à jour
  // (GET /telephony/status, rafraîchi par l'administration). La barre lisait
  // les siennes une fois au montage et ne revenait qu'au rechargement complet
  // après qu'un administrateur avait corrigé la configuration.
  const caps = tw.capabilities;
  if (caps && caps.browser_calls === false) return null;
  const { status, activeInfo, muted, held, callSeconds, endedCall, dialLabel: dialText } = tw;
  const metas = statusMeta(tw.label);
  const meta = metas[status] || metas.offline;
  const StatusIcon = meta.icon;
  const inCall = !!activeInfo;
  const layout = barLayout({ vw, n: fw?.windows.length || 0, inCall });
  // Copilote masqué (fermé par erreur) ou rangé dans la barre : un bouton le
  // fait revenir sans quitter l'appel. Absent tant qu'il est à l'écran.
  const copilotWin = fw?.windows.find((w) => w.id === "live-copilot");
  const copilotAway = inCall && (!copilotWin || copilotWin.minimized);
  const reopenCopilot = () => { if (copilotWin?.minimized) fw.restore(copilotWin.id); else showCopilot(); };

  const press = (d) => setDial((v) => (/[a-zA-ZÀ-ÿ]/.test(v) ? d : v + d));
  const backspace = () => setDial((v) => v.slice(0, -1));

  // Une seule surface ouverte à la fois : ouvrir le composeur range les
  // fenêtres déployées, et reprendre une fenêtre depuis la barre referme le
  // composeur (voir `onActivate` de la barre des tâches).
  const toggleDialer = () => {
    if (!open) fw?.minimizeAll();
    setOpen((o) => !o);
  };

  // Le champ accepte un numéro OU un nom : dès qu'une lettre est saisie, on
  // cherche dans les contacts au lieu d'interroger l'annuaire des numéros.
  const onSaisie = (valeur) => {
    setDial(valeur);
    setCible(null);
    if (/[a-zA-ZÀ-ÿ]/.test(valeur)) {
      setLookupName(null);
      clearTimeout(rechercheTimer.current);
      const q = valeur.trim();
      if (q.length < 2) { setSuggestions([]); return; }
      rechercheTimer.current = setTimeout(() => {
        api.get(`/contacts?q=${encodeURIComponent(q)}&limit=6`)
          .then(({ data }) => setSuggestions((data.items || []).filter((c) => c.telephone)))
          .catch(() => setSuggestions([]));
      }, 300);
    } else {
      setSuggestions([]);
      doLookup(valeur);
    }
  };

  const choisirContact = (c) => {
    setDial(c.telephone);
    setCible({ contact_id: c.id, company_id: c.company_id, company_name: c.company_nom });
    setLookupName([[c.prenom, c.nom].filter(Boolean).join(" "), c.company_nom].filter(Boolean).join(" · "));
    setSuggestions([]);
  };

  const doLookup = async (num) => {
    if (!num || num.length < 4) { setLookupName(null); return; }
    try {
      const { data } = await api.get(`/twilio/lookup?number=${encodeURIComponent(num)}`);
      // Le contact compte autant que l'entreprise : un numéro rattaché à une
      // seule fiche contact s'affichait comme « inconnu ».
      const contact = data.contact
        ? [data.contact.prenom, data.contact.nom].filter(Boolean).join(" ") : null;
      setLookupName([contact, data.company?.nom].filter(Boolean).join(" · ") || "Numéro inconnu");
    } catch { setLookupName(null); }
  };

  const call = () => {
    if (!dial.trim()) return;
    // La fiche choisie voyage avec l'appel : il est journalisé sur le bon
    // contact et la bonne entreprise, sans dépendre d'une reconnaissance
    // ultérieure du numéro.
    tw.makeCall(dial.trim(), cible || { company_name: lookupName });
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <>
      {endedCall && (endedCall.company_id || endedCall.number) && (
        <DispositionModal ended={endedCall} onClose={tw.clearEnded} />
      )}

      {/* Fiche créée depuis l'historique : les appels déjà journalisés de ce
          numéro lui sont rattachés côté serveur, on recharge donc la liste
          pour qu'elle cesse d'afficher « numéro inconnu ». */}
      {aCreer && (
        <UnknownCallerModal
          phone={aCreer}
          onClose={() => setACreer(null)}
          onCreated={() => { setACreer(null); chargerHistorique(); }}
        />
      )}

      {/* Barre collée au bas de l'écran, pleine largeur. left-0 sous 760 px :
          la barre latérale est un tiroir hors-flux en mobile, un décalage fixe
          laisserait un vide à gauche de l'écran. À partir de 760 px (palier
          shell-sm) on suit --sidebar-w (posée par CrmLayout) : 248px barre
          étendue, 76px en rail — sans ça la barre resterait calée sous le
          menu ou laisserait un trou une fois celui-ci réduit. Sous 760 px,
          --bottom-nav-h la remonte au-dessus de la barre d'onglets mobile. */}
      <div className="fixed bottom-[var(--bottom-nav-h,0px)] left-0 right-0 z-40 flex flex-col items-center gap-2.5 shell-sm:left-[var(--sidebar-w,76px)]"
        data-testid="softphone-bar">

        {/* Composeur — en-tête figé, corps défilant, largeur bornée et hauteur
            plafonnée à la fenêtre pour rester utilisable sur un écran de 360 px. */}
        {open && !inCall && (
          <div data-testid="softphone-dialer"
            className="flex max-h-[calc(100vh_-_9rem)] w-[min(392px,calc(100%_-_20px))] flex-col overflow-hidden rounded-xl border border-[rgba(15,23,42,.09)] bg-[rgba(252,252,253,.94)] shadow-[0_32px_64px_rgba(10,12,24,.22),0_2px_6px_rgba(10,12,24,.08)] backdrop-blur-[34px] backdrop-saturate-[180%] animate-fly-in">
            <div className="flex flex-none items-center gap-2 border-b border-[rgba(15,23,42,.07)] px-3 py-2.5">
              <span className="flex h-[26px] w-[26px] items-center justify-center rounded-[7px] bg-[#e8f1fd] text-[#005bd3]">
                <Phone size={14} />
              </span>
              <span className="flex-1 text-[13px] font-semibold text-[#0b1524]">Composeur</span>
              {/* Fermeture directe : on ne devrait pas avoir à viser une icône
                  à l'autre bout de l'écran pour refermer ce qu'on regarde. */}
              <button data-testid="dialer-close" onClick={() => setOpen(false)} aria-label="Fermer le composeur"
                className={cn(DIALER_ICON_BTN, "hover:bg-[#e81123] hover:text-white")}>
                <X size={15} />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3">
              <div className="mb-3 flex gap-1 rounded-lg bg-[rgba(15,23,42,.05)] p-[3px]">
                {[["clavier", "Clavier", Phone], ["historique", "Historique", History]].map(([k, label, Icon]) => (
                  <button key={k} data-testid={`dialer-tab-${k}`} onClick={() => setOnglet(k)}
                    className={cn("flex h-8 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-[13px] font-semibold transition-colors",
                      onglet === k ? "bg-white text-[#0b1524] shadow-[0_1px_2px_rgba(15,23,42,.14)]" : "text-[#5b6270] hover:text-[#0b1524]")}>
                    <Icon size={14} /> {label}
                  </button>
                ))}
              </div>

              {onglet === "historique" ? (
                <CallHistory
                  items={historique} loading={chargement} onReload={chargerHistorique}
                  sens={sens} onSens={setSens}
                  isAdmin={user?.role !== "commercial"}
                  onPick={(num) => { setDial(num); setOnglet("clavier"); doLookup(num); }}
                  onCall={(c) => { tw.makeCall(c.phone_number, { company_id: c.company_id, company_name: c.company_name }); setOpen(false); }}
                  onCreate={(num) => setACreer(num)}
                  canCall={status === "ready"}
                />
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="dialer-input" value={dial}
                      onChange={(e) => onSaisie(e.target.value)}
                      placeholder="Numéro ou nom du contact"
                      className="h-11 min-w-0 flex-1 rounded-lg border border-[#d0d0d0] bg-white px-3 font-mono text-[16px] text-[#0b1524] outline-none transition-all placeholder:font-sans placeholder:text-[14px] focus:border-[#005bd3] focus:ring-[3px] focus:ring-[rgba(0,91,211,.14)]"
                    />
                    <button onClick={backspace} aria-label="Effacer le dernier caractère"
                      className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-lg border border-[#d0d0d0] bg-white text-[#5b6270] transition-colors hover:bg-[#fafafa]">
                      <Delete size={17} />
                    </button>
                  </div>

                  {suggestions.length > 0 && (
                    <ul className="mt-2 max-h-[min(11rem,35vh)] overflow-y-auto rounded-lg border border-[#e3e3e3] bg-white divide-y divide-[rgba(15,23,42,.05)]"
                      data-testid="dialer-contact-suggestions">
                      {suggestions.map((c) => (
                        <li key={c.id}>
                          <button data-testid="dialer-contact-option" onClick={() => choisirContact(c)}
                            className="w-full px-3 py-2 text-left transition-colors hover:bg-[#f2f4f7]">
                            <span className="block truncate text-[13px] font-semibold text-[#1f2530]">
                              {[c.prenom, c.nom].filter(Boolean).join(" ") || c.telephone}
                            </span>
                            <span className="block truncate text-[11px] text-[#8a8f98]">
                              <span className="font-mono">{c.telephone}</span>
                              {[c.company_nom, c.fonction].filter(Boolean).map((x) => ` · ${x}`).join("")}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-2 flex min-h-[18px] items-center gap-1.5 text-xs text-[#5b6270]">
                    {lookupName && (
                      <>
                        {cible ? <UserRound size={12} className="shrink-0 text-[#005bd3]" />
                               : <Building2 size={12} className="shrink-0 text-[#8a8f98]" />}
                        <span className="truncate">{lookupName}</span>
                      </>
                    )}
                  </div>

                  <div className="mt-2 grid grid-cols-3 gap-1.5">
                    {KEYPAD.map(([k, lettres]) => (
                      <button key={k} data-testid={`dialpad-${k}`} onClick={() => press(k)}
                        className="rounded-lg border border-[rgba(15,23,42,.08)] bg-white py-[9px] transition-all hover:border-[#d0d0d0] hover:bg-[#f2f4f7] active:scale-[.97]">
                        <span className="block text-lg font-medium leading-none text-[#0b1524]">{k}</span>
                        <span className="mt-[3px] block h-2.5 text-[9px] leading-none tracking-[.16em] text-[#b7bcc5]">{lettres}</span>
                      </button>
                    ))}
                  </div>

                  <button data-testid="dialer-call-btn" onClick={call} disabled={status !== "ready" || !dial.trim()}
                    title={status !== "ready" ? "Téléphone non connecté" : undefined}
                    className="mt-2.5 flex h-[46px] w-full items-center justify-center gap-2 rounded-[9px] bg-[#008060] text-sm font-bold text-white transition-all hover:bg-[#004c3f] active:scale-[.99] disabled:cursor-not-allowed disabled:opacity-40">
                    <Phone size={17} /> Appeler
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Barre des tâches : état du téléphone à gauche, fenêtres au centre,
            appel en cours et actions à droite. Une seule ligne, jamais de
            retour à la ligne — c'est `layout` qui compresse. */}
        <div ref={setBarEl}
          className={cn("flex h-14 w-full flex-nowrap items-center border-t border-white/[.09] bg-slate-900 text-white shadow-[0_-10px_30px_rgba(0,0,0,.25)] backdrop-blur-[32px] backdrop-saturate-[180%]",
            layout.narrow ? "gap-1.5" : "gap-2", layout.xs ? "px-2" : "px-3")}>

          <div className={cn("flex h-10 flex-none items-center gap-2 rounded-lg", layout.tight ? "px-1" : "pl-1.5 pr-2.5")}
            data-testid="softphone-status" title={meta.label}>
            <span className="relative flex h-[9px] w-[9px]">
              <span className={cn("absolute inset-0 rounded-full opacity-35", meta.dot,
                (status === "ready" || status === "reconnecting") && "animate-pulse")} />
              <span className={cn("relative h-[9px] w-[9px] rounded-full", meta.dot)} />
            </span>
            <StatusIcon size={16} className={cn(status === "reconnecting" ? "text-orange-400" : "text-[#c8ccd4]",
              (status === "connecting" || status === "reconnecting") && "animate-spin")} />
            {!layout.tight && (
              <span className={cn("whitespace-nowrap text-[13px] font-medium",
                status === "reconnecting" ? "font-semibold text-orange-400" : "text-[#d7dae0]")}>
                {meta.label}
              </span>
            )}
          </div>

          {!layout.xs && <span className="h-6 w-px flex-none bg-white/[.12]" />}

          {/* Fenêtres (préparation d'appel, copilote) : la barre du bas est la
              seule permanente de l'application, donc l'endroit où l'on va
              rechercher ce qu'on a rangé. L'enveloppe garde l'espace central
              même quand aucune fenêtre n'est montée. */}
          <div className="flex min-w-0 flex-1 items-center">
            <FloatingWindowDock className="min-w-0" iconOnly={layout.iconOnly} maxVisible={layout.maxVisible}
              labelActive={layout.labelActive} onActivate={() => setOpen(false)} />
          </div>

          {inCall && (
            <div className="flex h-10 flex-none items-center gap-2.5 rounded-lg border border-[rgba(0,190,140,.35)] bg-[rgba(0,128,96,.22)] pl-3 pr-1.5"
              data-testid="softphone-active-call">
              <PhoneCall size={15} className="animate-pulse text-[#4ade9f]" />
              {!layout.tight && (
                <span className="max-w-[150px] truncate text-[13px] font-semibold text-[#f2f4f7]">
                  {activeInfo.company_name || activeInfo.number}
                </span>
              )}
              {status === "connecting" && activeInfo.direction === "SORTANT" ? (
                <span className="text-[12.5px] text-[#ffd48a]" data-testid="active-call-dial">{dialText || "Connexion…"}</span>
              ) : (
                <span className="font-mono text-[13px] text-[#8ef0c4]" data-testid="active-call-timer">{fmtDur(callSeconds)}</span>
              )}
              {!layout.tight && (
                <span className="text-xs text-[#9aa0ab]">({activeInfo.direction === "SORTANT" ? "sortant" : "entrant"})</span>
              )}
              {copilotAway && (
                <button data-testid="softphone-copilot-btn" onClick={reopenCopilot}
                  title="Rouvrir le copilote" aria-label="Rouvrir le copilote"
                  className={cn(CALL_ICON_BTN, "bg-[#4a3ae0]/80 text-white hover:bg-[#4a3ae0]", !layout.tight && "w-auto gap-1.5 px-2.5")}>
                  <Sparkles size={15} />{!layout.tight && <span className="text-[12.5px] font-medium">Copilote</span>}
                </button>
              )}
              <button data-testid="softphone-mute-btn" onClick={tw.toggleMute}
                title={muted ? "Réactiver le micro" : "Couper le micro"} aria-label={muted ? "Réactiver le micro" : "Couper le micro"}
                className={cn(CALL_ICON_BTN, muted ? "bg-amber-500 text-white hover:bg-amber-400" : "bg-white/[.12] hover:bg-white/20")}>
                {muted ? <MicOff size={15} /> : <Mic size={15} />}
              </button>
              {/* Mise en attente : seulement chez les fournisseurs qui la
                  déclarent (Telnyx) — l'interface s'adapte aux capacités. */}
              {caps?.hold && (
                <button data-testid="softphone-hold-btn" onClick={tw.toggleHold}
                  title={held ? "Reprendre l'appel" : "Mettre en attente"} aria-label={held ? "Reprendre l'appel" : "Mettre en attente"}
                  className={cn(CALL_ICON_BTN, held ? "bg-amber-500 text-white hover:bg-amber-400" : "bg-white/[.12] hover:bg-white/20")}>
                  {held ? <Play size={15} /> : <Pause size={15} />}
                </button>
              )}
              <button data-testid="softphone-hangup-btn" onClick={tw.hangup} title="Raccrocher" aria-label="Raccrocher"
                className={cn("inline-flex h-[30px] items-center justify-center gap-1.5 rounded-[7px] bg-[#d72c0d] text-[13px] font-semibold text-white transition-colors hover:bg-[#b32200]",
                  layout.tight ? "w-[30px]" : "px-3")}>
                <PhoneOff size={14} />{!layout.tight && "Raccrocher"}
              </button>
            </div>
          )}

          <div className="flex flex-none items-center gap-1.5">
            {status === "offline" && (
              <button data-testid="softphone-connect-btn" onClick={tw.goOnline} title="Se connecter"
                className={cn(BAR_PRIMARY, layout.tight ? "w-11" : "gap-2 px-3.5")}>
                <Power size={15} />{!layout.tight && "Se connecter"}
              </button>
            )}
            {/* Déconnexion possible dans tous les états actifs, pas seulement
                « ready » : en « reconnecting » (signal perdu) et en « error »,
                aucun des deux boutons ne s'affichait et l'utilisateur restait
                coincé sur la bannière de reconnexion sans pouvoir en sortir. */}
            {!inCall && !["offline", "unconfigured"].includes(status) && (
              <button data-testid="softphone-offline-btn" onClick={tw.goOffline} title="Se déconnecter"
                className={cn(BAR_GHOST, layout.tight ? "w-11" : "gap-[7px] px-3")}>
                <Power size={15} />{!layout.tight && "Se déconnecter"}
              </button>
            )}
            {!inCall && status !== "unconfigured" && (
              <button data-testid="softphone-toggle-dialer" onClick={toggleDialer} title="Composer" aria-expanded={open}
                className={cn(BAR_PRIMARY, layout.tight ? "w-11" : "gap-2 px-3.5")}>
                <Phone size={16} className="flex-none" />
                {!layout.tight && (
                  <>
                    <span>Composer</span>
                    {open ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
